import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PAYMENT_STATUS_ACTIVE } from "../../../constants/constant";
import { setIsFromSettings, setIsRiskFreeTier, setPlanDetails } from "../../../redux/features/userSlice";
import { ErrorMessage } from "../../../utils/commonUtils";
import { SUCCESS } from "../../../utils/constants";
import { SettingsActions } from "../../api/RequestUtil";
import AlertService from "../../common/Alert/alertService";
import CustomDialog from "../../common/CustomDialog";
import AccountDetails from "./AccountDetails";
import SelectPlan from "./SelectPlan";

const ChangePlan = ({ open, handleClose, handleCloseButtonClick, refetchData = () => {} }) => {
  const dispatch = useDispatch();
  const { warning, error, success } = AlertService();
  const [loader, setLoader] = useState(false);

  const {
    currentPlan: { planId, upcomingPlanId, planExpiryDate, nextPaymentDate, planName },
    accountOwnerDetails: { firstName, lastName, email, phone, companyName, companyNumber, address },
  } = useSelector((state) => state.settings);
  const { isRiskFreeTier } = useSelector((state) => state.user);

  const [selectedPlanId, setSelectedPlanId] = useState(planId);
  const [currentStepCount, setCurrentStepCount] = useState(0);

  // Submit user details to Marketo form for eWay onboarding
  const submitToMktoForms = () => {
    try {
      // Extract country from address
      let country = "";
      if (address) {
        const addressParts = address.split(",");
        country = addressParts[addressParts.length - 1].trim();
      }

      console.log("=== Marketo Form Submission ===");
      console.log("Country:", country);
      console.log("Form data:", {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
        Company: companyName,
        eWAY_Branch__c: country,
        ABN__c_lead: companyNumber,
      });

      // Check if MktoForms2 is available
      if (!window.MktoForms2) {
        console.error("❌ MktoForms2 not found on window object!");
        return;
      }

      console.log("MktoForms2 available:", window.MktoForms2);
      console.log("All forms:", window.MktoForms2.allForms());

      if (country === "New Zealand" || country === "Australia") {
        const allForms = window.MktoForms2.allForms();
        if (!allForms || allForms.length === 0) {
          console.error("❌ No Marketo forms found!");
          return;
        }

        const myForm = allForms[0];
        console.log("Marketo form found:", myForm);
        console.log("Form ID:", myForm.getId());

        console.log("Adding hidden fields...");
        myForm.addHiddenFields({
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Phone: phone,
          Company: companyName,
          eWAY_Branch__c: country,
          ABN__c_lead: companyNumber,
        });
        console.log("Hidden fields added successfully");
        console.log("Form values after adding fields:", myForm.getValues());

        // Submit the SAME form instance (not a new one from whenReady)
        myForm.onSuccess((values, followUpUrl) => {
          console.log("✅ Marketo form submitted successfully!");
          console.log("Submitted values:", values);
          console.log("Follow-up URL:", followUpUrl);
          return false; // Prevent redirect
        });

        console.log("Submitting form now...");
        myForm.submit();
        console.log("✅ Form submitted");
      } else {
        console.log("⚠️ Country not NZ or AU, skipping Marketo submission");
      }
    } catch (err) {
      console.error("❌ Error submitting to Marketo forms:", err);
      console.error("Error stack:", err.stack);
    }
  };

  const onCloseButtonClick = () => {
    handleCloseButtonClick ? handleCloseButtonClick() : handleClose();
    setCurrentStepCount(0);
    setSelectedPlanId(planId);
  };

  const handleStopUsingNativusCoach = () => {
    setLoader(true);

    SettingsActions.stopPlan()
      .then(({ data }) => {
        if (data.status === SUCCESS) {
          success(data.message);
          handleClose();
          setCurrentStepCount(0);
          refetchData && refetchData();
        }
      })
      .catch(({ data }) => error(ErrorMessage(data)))
      .finally(() => setLoader(false));
  };

  const changePlanSteps = [
    {
      step: 0,
      label: "Change Plan - Please select a New Plan",
      component: <SelectPlan selectedPlanId={selectedPlanId} setSelectedPlanId={setSelectedPlanId} />,
      onNextClick: () => {
        if (!selectedPlanId) {
          warning("Please select any of plan");
          return;
        }
        // Allow reactivating the same plan if subscription is cancelled (has expiry date but no next payment)
        const isCancelled = planExpiryDate && !nextPaymentDate;
        if (selectedPlanId === planId && !isCancelled) {
          warning("Please choose different plan to continue.");
          return;
        }
        if (selectedPlanId === upcomingPlanId) {
          warning("You are already subscribed to this plan.");
          return;
        }
        if (selectedPlanId) setCurrentStepCount(currentStepCount + 1);
      },
      onPreviousClick: () => {
        handleClose();
        setSelectedPlanId(planId);
      },
      cancelTitle: "Cancel",
      saveTitle: "Next",
    },
    {
      step: 1,
      label: "Change Plan - Your New Payment Schedule",
      component: <AccountDetails selectedPlanId={selectedPlanId} />,
      onNextClick: () => {
        setLoader(true);
        SettingsActions.changePlan({
          new_plan_id: selectedPlanId,
        })
          .then(({ data }) => {
            if (data.status === SUCCESS) {
              success(data.message);

              // If upgrading from Risk Free Trial, submit to Marketo for eWay onboarding
              console.log("Checking if should submit to Marketo:", {
                isRiskFreeTier,
                planName,
                willSubmit: isRiskFreeTier,
              });

              if (isRiskFreeTier) {
                console.log("🎯 Triggering Marketo form submission for Risk Free Trial upgrade");
                submitToMktoForms();
              }

              handleClose();
              setCurrentStepCount(0);
              refetchData && refetchData();

              if (isRiskFreeTier) {
                dispatch(setIsRiskFreeTier(false));
                dispatch(
                  setPlanDetails({
                    status: PAYMENT_STATUS_ACTIVE,
                    planRemainingGraceDays: null,
                  })
                );
              }

              dispatch(setIsFromSettings(false));
            }
          })
          .catch(({ data }) => error(ErrorMessage(data)))
          .finally(() => setLoader(false));
      },
      onPreviousClick: () => setCurrentStepCount(currentStepCount - 1),
      cancelTitle: "Cancel",
      saveTitle: "Confirm",
    },
  ];

  const currentStep = changePlanSteps[currentStepCount];

  return (
    <CustomDialog
      title={currentStep.label}
      open={open}
      onCloseButtonClick={onCloseButtonClick}
      handleClose={currentStep.onPreviousClick}
      dialogWidth={currentStepCount === 0 ? "lg" : "md"}
      handleSaveTitle={currentStep.saveTitle}
      handleCancelTitle={currentStep.cancelTitle}
      handleSave={currentStep.onNextClick}
      reverseButtons
      loader={loader}
      thirdButtonText="Stop using Nativus Coach"
      isThirdButtonExist={currentStepCount === 0}
      handleThirdButtonOnClick={handleStopUsingNativusCoach}
      thirdButtonLoader={true}
    >
      {currentStep.component}
    </CustomDialog>
  );
};

ChangePlan.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleCloseButtonClick: PropTypes.func,
  refetchData: PropTypes.func,
};

export default ChangePlan;
